import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import ToggleTabGroup from '../components/atoms/ToggleTabGroup';
import AgencyIndicator from '../components/molecules/AgencyIndicator';
import { DeleteDialog, PageHeader, PageWrapper } from '../components/partials';
import AppsList from '../components/partials/VerticalList/AppsList';
import { Button, Dialog, EmptyState, Shelf, Spinner, WarningMessage } from '../components/widgets';
import PermissionsWrapper from '../containers/PermissionsWrapper';
import { getIsAgency, isUserAdNetwork } from '../selectors/user';
import { MANAGE_LINKS_EVENT_PREFIX } from '../utils/general';
import LinkGroupContainers from './components/LinkGroupContainers';
import SubDomainsPage from './components/manageDomainsShelf/SubDomainsPage';
import TopArea from './components/TopArea';
import { TOGGLE_TAB_GROUP_OPTIONS } from './consts';
import LinkEditorShelf from './containers/LinkEditorShelf';
import css from './page.css';
import GeneralPopup, { PopupTypes } from '../components/organisms/GeneralPopup';

const FAQ_LINKS = { faqLink: 'https://support.singular.net/hc/en-us/articles/360030934212-Singular-Links-FAQ' };

const AGENCY_SETUP_HEADER_CLASS = 'agencySetupHeader';

const LinkManagement = props => {
    const {
        loading = true,
        loadingLinks = true,
        searching = true,
        onAppSelected = () => {},
        groups = [],
        onAppFilterChanged = () => {},
        appsFilter = '',
        onExpandClick = () => {},
        onCreateLinkClicked = () => {},
        onEditClick = () => {},
        onViewClick = () => {},
        onDuplicateClicked = () => {},
        shelf = { open: false },
        onTopButtonClicked = () => {},
        subDomainsPage = { open: false },
        emptyState = {},
        onEmptyStateAction = () => {},
        oldLinksCount = 0,
        agenciesLinksCount = 0,
        apiLinksCount = 0,
        translate,
        selectedToggleTab = 'ALL_LINKS',
        onToggleTabChanged = () => {},
        allLinksCount = 0,
        standardLinksCount = 0,
        showApiToggle = false,
        showApiLinkCount = true,
        apps = [],
        onCloseShelf = () => {},
        onArchiveClicked = () => {},
        onDeleteLinkClicked = () => {},
        missingAgenciesNames = [],
    } = props;
    const [shelfFormDirty, setShelfFormDirty] = useState(false);
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [openGroups, setOpenGroups] = useState({
        custom: false,
    });

    const archiveLinkDataRef = useRef(null);
    const deleteLinkDataRef = useRef(null);
    const isAgency = useSelector(getIsAgency);
    const isPartner = useSelector(isUserAdNetwork);
    const showMissingAgenciesWarning = !isAgency && !isPartner && missingAgenciesNames.length > 0;

    const onFormDirtyChanged = useCallback(dirty => {
        setShelfFormDirty(dirty);
    }, []);

    const handleArchiveLink = useCallback((linkId, singularLink, campaignId) => {
        archiveLinkDataRef.current = {
            singularLink,
            linkId,
            campaignId,
        };
        setShowArchiveDialog(true);
    }, []);

    const handleDeleteLink = useCallback((linkId, singularLink, campaignId) => {
        deleteLinkDataRef.current = {
            singularLink,
            linkId,
            campaignId,
        };
        setShowDeleteDialog(true);
    });

    const handleArchiveLinkAccept = useCallback(() => {
        if (archiveLinkDataRef.current) {
            onArchiveClicked(
                archiveLinkDataRef.current.linkId,
                archiveLinkDataRef.current.singularLink,
                archiveLinkDataRef.current.campaignId
            );
        }
        setShowArchiveDialog(false);
        archiveLinkDataRef.current = null;
    }, [onArchiveClicked]);

    const handleArchiveLinkReject = useCallback(() => {
        setShowArchiveDialog(false);
        deleteLinkDataRef.current = null;
    }, []);

    const handleDeleteLinkAccept = useCallback(() => {
        if (deleteLinkDataRef.current) {
            onDeleteLinkClicked(deleteLinkDataRef.current.linkId);
        }
        setShowDeleteDialog(false);
        deleteLinkDataRef.current = null;
    }, [onDeleteLinkClicked]);

    const handleDeleteLinkReject = useCallback(() => {
        setShowDeleteDialog(false);
        deleteLinkDataRef.current = null;
    }, []);

    const handleGroupTitleClicked = useCallback(groupName => {
        setOpenGroups(prev => {
            if (!(groupName in prev)) {
                return {
                    ...prev,
                    [groupName]: false,
                };
            }
            return {
                ...prev,
                [groupName]: !prev[groupName],
            };
        });
    }, []);

    const isGroupOpen = useCallback(
        group => {
            return (!(group.name in openGroups) && group.open) || openGroups[group.name];
        },
        [openGroups]
    );

    const getToggleCountByValue = useCallback(count => {
        if (count === null) {
            return 'N/A';
        }
        if (count < 10000) {
            return count;
        }
        return '9999+';
    }, []);

    const getAppListWithContent = useCallback(() => {
        return apps.map(app => ({
            ...app,
            lastUpdate: app.lastUpdate,
            content: app.content ? (
                <div key={app.id} className={css.appSummaryContent}>
                    <Translate
                        id="STATIC.PAGES.MANAGE_LINKS.CUSTOM_LINKS_COUNT"
                        data={{ customCount: app.content.customCount }}
                    />
                    <br />
                    <Translate
                        id="STATIC.PAGES.MANAGE_LINKS.PARTNER_LINKS_COUNT"
                        data={{ partnerCount: app.content.partnerCount }}
                    />
                    <AgencyIndicator
                        hasMissingAgencies={app.content.missingAgenciesNames.length > 0}
                        hasAgency={app.content.agencies.length > 0}
                        missingAgencyTooltipKey="STATIC.PAGES.MANAGE_LINKS.MISSING_AGENCY_LINKS"
                        hasAgencyTooltipKey="STATIC.PAGES.MANAGE_LINKS.HAS_AGENCY_LINKS"
                    />
                </div>
            ) : (
                <></>
            ),
        }));
    }, [apps]);

    const closeShelf = useCallback(() => {
        setShelfFormDirty(false);
        setShowUnsavedChangesDialog(false);
        archiveLinkDataRef.current = null;
        onCloseShelf();
    }, [onCloseShelf]);

    const onCloseShelfHandler = useCallback(() => {
        if (shelfFormDirty) {
            setShowUnsavedChangesDialog(true);
            return;
        }

        closeShelf();
    }, [shelfFormDirty, closeShelf]);

    const toggleTabGroupOptions = useMemo(() => {
        return [
            {
                option: TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS,
                label: translate('STATIC.PAGES.MANAGE_LINKS.ALL_LINKS_TOGGLE_LABEL', {
                    count: getToggleCountByValue(allLinksCount),
                }),
                ...(apiLinksCount > 0 && { tooltip: translate('STATIC.PAGES.MANAGE_LINKS.ALL_LINKS_TOGGLE_TOOLTIP') }),
            },
            ...(allLinksCount !== standardLinksCount && standardLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.STANDARD_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.STANDARD_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(standardLinksCount),
                          }),
                      },
                  ]
                : []),
            ...(agenciesLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.AGENCY_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.AGENCY_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(agenciesLinksCount),
                          }),
                      },
                  ]
                : []),
            ...(oldLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.OLD_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.OLD_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(oldLinksCount),
                          }),
                          tooltip: translate('STATIC.PAGES.MANAGE_LINKS.LEGACY_TOGGLE_TOOLTIP'),
                          disabled: emptyState?.oldLinksDisabled,
                      },
                  ]
                : []),
            ...(showApiToggle
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.API_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.API_LINKS_TOGGLE_LABEL', {
                              count: showApiLinkCount ? getToggleCountByValue(apiLinksCount) : '...',
                          }),
                      },
                  ]
                : []),
        ];
    }, [
        translate,
        allLinksCount,
        apiLinksCount,
        agenciesLinksCount,
        oldLinksCount,
        standardLinksCount,
        emptyState,
        showApiToggle,
        showApiLinkCount,
        getToggleCountByValue,
    ]);

    if (subDomainsPage?.open) {
        return <SubDomainsPage {...subDomainsPage} />;
    }

    if (loading) {
        return <Spinner show expanded />;
    }

    const showToggleTabGroup =
        toggleTabGroupOptions.length > 2 ||
        (toggleTabGroupOptions.length === 2 &&
            allLinksCount !== Math.max(apiLinksCount, agenciesLinksCount, oldLinksCount)) ||
        showApiToggle;

    return (
        <PageWrapper className={css.container} disablePadding>
            <div className={css.topArea}>
                <TopArea
                    onAppFilterChanged={onAppFilterChanged}
                    search={appsFilter}
                    onClick={onTopButtonClicked}
                    emptyState={emptyState}
                    searching={searching}
                />
            </div>
            <div className={css.mainArea}>
                <AppsList
                    sortApps={e => e}
                    className={css.leftArea}
                    apps={getAppListWithContent()}
                    appsFilter={appsFilter}
                    onAppSelected={onAppSelected}
                    disableAnimation
                />
                <div className={css.rightArea}>
                    {showMissingAgenciesWarning && (
                        <WarningMessage
                            message="STATIC.PAGES.MANAGE_LINKS.MISSING_AGENCIES_MESSAGE"
                            show
                            showIcon={false}
                            showSpacer={false}
                            type="warning"
                            className={css.missingAgenciesWarning}
                            textReplace={{
                                agenciesNames: missingAgenciesNames.join(', '),
                            }}
                        />
                    )}
                    <div className={classNames(css.rightAreaTop, { [css.withWarning]: showMissingAgenciesWarning })}>
                        <PageHeader
                            subText={FAQ_LINKS}
                            text="STATIC.PAGES.MANAGE_LINKS.AVAILABLE_LINKS"
                            mixpanelEventPrefix={MANAGE_LINKS_EVENT_PREFIX}
                        />
                        <div className={css.actions}>
                            <div>
                                <PermissionsWrapper write requiredSingleAccessLevel>
                                    <Button onClick={onCreateLinkClicked} disabled={emptyState?.createLinkDisabled}>
                                        <Translate id="STATIC.PAGES.MANAGE_LINKS.CREATE_LINK_BUTTON" />
                                    </Button>
                                </PermissionsWrapper>
                            </div>
                            {showToggleTabGroup && (
                                <ToggleTabGroup
                                    options={toggleTabGroupOptions}
                                    selectedOption={selectedToggleTab}
                                    onToggle={onToggleTabChanged}
                                />
                            )}
                        </div>
                    </div>
                    <div className={css.rightAreaMain}>
                        {loadingLinks && <Spinner show style={{ marginTop: 120 }} />}
                        {!loadingLinks && !emptyState && (
                            <LinkGroupContainers
                                groups={groups}
                                isGroupOpen={isGroupOpen}
                                onTitleClick={handleGroupTitleClicked}
                                handleArchiveLink={handleArchiveLink}
                                handleDeleteLink={handleDeleteLink}
                                onEditClick={onEditClick}
                                onViewClick={onViewClick}
                                onDuplicateClicked={onDuplicateClicked}
                                onExpandClick={onExpandClick}
                            />
                        )}
                        {!loadingLinks && emptyState && (
                            <EmptyState
                                style={{ marginTop: 120, textAlign: 'center' }}
                                {...emptyState.data}
                                onActionTriggered={onEmptyStateAction}
                            />
                        )}
                    </div>
                </div>
            </div>
            <Shelf
                {...shelf}
                shelfSize="medium"
                headerClassName={shelf.isForAgency ? AGENCY_SETUP_HEADER_CLASS : shelf.headerClassName}
                onClose={onCloseShelfHandler}
            >
                <LinkEditorShelf onFormDirtyChanged={onFormDirtyChanged} onRequestClose={onCloseShelfHandler} />
            </Shelf>
            <GeneralPopup
                open={showArchiveDialog}
                type="delete"
                title="STATIC.PAGES.VIEW_TRACKING_LINKS.ARCHIVE_WARNING"
                text="STATIC.PAGES.VIEW_TRACKING_LINKS.ARCHIVE_MESSAGE"
                acceptText="STATIC.PAGES.VIEW_TRACKING_LINKS.ARCHIVE_ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.VIEW_TRACKING_LINKS.REJECT_BUTTON_TEXT"
                onAccept={handleArchiveLinkAccept}
                onReject={handleArchiveLinkReject}
                showSpinner={false}
            />
            <GeneralPopup
                open={showDeleteDialog}
                type="delete"
                title="STATIC.PAGES.VIEW_TRACKING_LINKS.DELETE_WARNING"
                text="STATIC.PAGES.VIEW_TRACKING_LINKS.DELETE_MESSAGE"
                acceptText="STATIC.PAGES.VIEW_TRACKING_LINKS.DELETE_ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.VIEW_TRACKING_LINKS.REJECT_BUTTON_TEXT"
                onAccept={handleDeleteLinkAccept}
                onReject={handleDeleteLinkReject}
                showSpinner={false}
            />
            <GeneralPopup
                open={showUnsavedChangesDialog}
                type={PopupTypes.WARNING_WITH_CANCEL}
                title="STATIC.NAVIGATION_BLOCKER.TITLE"
                text="STATIC.NAVIGATION_BLOCKER.MESSAGE"
                acceptText="STATIC.NAVIGATION_BLOCKER.ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.NAVIGATION_BLOCKER.REJECT_BUTTON_TEXT"
                onAccept={closeShelf}
                onReject={() => setShowUnsavedChangesDialog(false)}
                showSpinner={false}
            />
        </PageWrapper>
    );
};

LinkManagement.propTypes = {
    loading: PropTypes.bool,
    loadingLinks: PropTypes.bool,
    searching: PropTypes.bool,
    apps: PropTypes.arrayOf(PropTypes.any),
    onAppSelected: PropTypes.func,
    onAppFilterChanged: PropTypes.func,
    onExpandClick: PropTypes.func,
    onCreateLinkClicked: PropTypes.func,
    onCloseShelf: PropTypes.func,
    onEditClick: PropTypes.func,
    onViewClick: PropTypes.func,
    appsFilter: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.any),
    shelf: PropTypes.objectOf(PropTypes.any),
    onTopButtonClicked: PropTypes.func,
    subDomainsPage: PropTypes.objectOf(PropTypes.any),
    onDuplicateClicked: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    onDeleteLinkClicked: PropTypes.func,
    onEmptyStateAction: PropTypes.func,
    allLinksCount: PropTypes.number,
    standardLinksCount: PropTypes.number,
    oldLinksCount: PropTypes.number,
    agenciesLinksCount: PropTypes.number,
    apiLinksCount: PropTypes.number,
    translate: PropTypes.func,
    emptyState: PropTypes.objectOf(PropTypes.any),
    selectedToggleTab: PropTypes.string,
    onToggleTabChanged: PropTypes.func,
    showApiToggle: PropTypes.bool,
    showApiLinkCount: PropTypes.bool,
    missingAgenciesNames: PropTypes.arrayOf(PropTypes.string),
};

export default withLocalize(LinkManagement);
